import React, {
  useState,
  useCallback,
  useImperativeHandle,
  useEffect,
} from "react";
import { Radio, Checkbox, Input } from "antd";
import { ARRejectionConfig } from "./constants";

// antd v3 doesn't export CheckboxValueType from a stable path; this matches
// its runtime shape.
type CheckboxValue = string | number | boolean;

const CONTAINER_COLUMN_STYLE: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
};

const RADIO_ROW_STYLE: React.CSSProperties = {
  display: "flex",
  gap: "1rem",
  marginBottom: "0.5rem",
};

const RADIO_LABEL_STYLE: React.CSSProperties = {
  color: "rgba(0, 0, 0, 0.85)",
  fontSize: "0.75rem",
};

const CHECKLIST_ROW_STYLE: React.CSSProperties = {
  display: "flex",
  flexDirection: "row",
  gap: "0.5rem",
  marginBottom: "0.375rem",
};

const CHECKLIST_BOX_STYLE: React.CSSProperties = {
  border: "0.0625rem solid #e4e7eb",
  borderRadius: "0.25rem",
  padding: "0.5rem 0.625rem",
  background: "#fafafa",
  flex: 1,
  minWidth: 0,
};

const SECTION_HEADER_STYLE: React.CSSProperties = {
  fontSize: "0.6875rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.03125rem",
  color: "rgba(0, 0, 0, 0.65)",
  marginBottom: "0.375rem",
};

const CHECKBOX_GROUP_STYLE: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "0.25rem",
};

// antd v3's default rule adds margin-left: 8px between sibling
// Checkbox.Group items, which breaks vertical alignment.
const CHECKBOX_ITEM_STYLE: React.CSSProperties = {
  marginLeft: 0,
  color: "rgba(0, 0, 0, 0.85)",
  fontSize: "0.75rem",
};

const INTERNAL_NOTE_LABEL_STYLE: React.CSSProperties = {
  fontSize: "0.6875rem",
  fontWeight: 500,
  color: "rgba(0, 0, 0, 0.65)",
  margin: "0.375rem 0 0.25rem",
};

const TEXT_AREA_STYLE: React.CSSProperties = {
  border: "solid 0.0625rem #e4e7eb",
  marginTop: "0.125rem",
  fontSize: "0.75rem",
};

const { TextArea } = Input;

export interface ReviewARSubmissionInputValues {
  approved: boolean;
  // Combined Missing-information + Invalid-submission selections, deduped.
  reason: Set<string>;
  // Admin-only — never shown to the member.
  note: string | null;
  isValid: boolean;
}

export interface ReviewARSubmissionInputRef {
  getSubmissionReviewInput: () => ReviewARSubmissionInputValues;
  isValid: () => boolean;
}

export interface ReviewARSubmissionInputProps {
  config: ARRejectionConfig;
  initialMissing?: string[];
  initialQuality?: string[];
  onValidityChange?: (isValid: boolean) => void;
  onApprovalChange?: (approved: boolean) => void;
}

const ReviewARSubmissionInput: React.RefForwardingComponent<
  ReviewARSubmissionInputRef,
  ReviewARSubmissionInputProps
> = (
  {
    config,
    initialMissing = [],
    initialQuality = [],
    onValidityChange,
    onApprovalChange,
  },
  ref,
) => {
  const [approved, setApproved] = useState<boolean>(true);
  const [missingSelected, setMissingSelected] = useState<CheckboxValue[]>(
    initialMissing,
  );
  const [qualitySelected, setQualitySelected] = useState<CheckboxValue[]>(
    initialQuality,
  );
  const [internalNote, setInternalNote] = useState<string | null>(null);

  const totalSelected = missingSelected.length + qualitySelected.length;
  const isValid = approved || totalSelected > 0;

  useEffect(
    () => {
      if (onValidityChange) onValidityChange(isValid);
    },
    [isValid, onValidityChange],
  );

  useEffect(
    () => {
      if (onApprovalChange) onApprovalChange(approved);
    },
    [approved, onApprovalChange],
  );

  // Checking a reason auto-flips the decision to Reject. Unchecking reasons
  // never auto-flips back to Approve — the reviewer must opt in explicitly.
  const handleMissingChange = useCallback((vals: CheckboxValue[]) => {
    setMissingSelected(vals);
    if (vals.length > 0) setApproved(false);
  }, []);

  const handleQualityChange = useCallback((vals: CheckboxValue[]) => {
    setQualitySelected(vals);
    if (vals.length > 0) setApproved(false);
  }, []);

  const handleDecisionChange = useCallback((value: boolean) => {
    setApproved(value);
    if (value) {
      setMissingSelected([]);
      setQualitySelected([]);
    }
  }, []);

  const getSubmissionReviewInput = useCallback(
    (): ReviewARSubmissionInputValues => ({
      approved,
      reason: approved
        ? new Set<string>()
        : new Set<string>([
            ...missingSelected.map(String),
            ...qualitySelected.map(String),
          ]),
      note: internalNote,
      isValid,
    }),
    [approved, missingSelected, qualitySelected, internalNote, isValid],
  );

  useImperativeHandle(
    ref,
    () => ({
      getSubmissionReviewInput,
      isValid: () => isValid,
    }),
    [getSubmissionReviewInput, isValid],
  );

  return (
    <div style={CONTAINER_COLUMN_STYLE}>
      <Radio.Group
        value={approved}
        onChange={({ target: { value } }): void => handleDecisionChange(value)}
        style={RADIO_ROW_STYLE}
      >
        <Radio value style={RADIO_LABEL_STYLE}>
          <strong>Approve</strong>
        </Radio>
        <Radio value={false} style={RADIO_LABEL_STYLE}>
          <strong>Reject</strong>
        </Radio>
      </Radio.Group>

      <div style={CHECKLIST_ROW_STYLE}>
        <div style={CHECKLIST_BOX_STYLE}>
          <div style={SECTION_HEADER_STYLE}>Missing information</div>
          <Checkbox.Group
            style={CHECKBOX_GROUP_STYLE}
            value={missingSelected}
            onChange={handleMissingChange}
          >
            {config.missingInformation.map((item) => (
              <Checkbox key={item} value={item} style={CHECKBOX_ITEM_STYLE}>
                {item}
              </Checkbox>
            ))}
          </Checkbox.Group>
        </div>

        <div style={CHECKLIST_BOX_STYLE}>
          <div style={SECTION_HEADER_STYLE}>Invalid submission</div>
          <Checkbox.Group
            style={CHECKBOX_GROUP_STYLE}
            value={qualitySelected}
            onChange={handleQualityChange}
          >
            {config.invalidSubmission.map((item) => (
              <Checkbox key={item} value={item} style={CHECKBOX_ITEM_STYLE}>
                {item}
              </Checkbox>
            ))}
          </Checkbox.Group>
        </div>
      </div>

      <div style={INTERNAL_NOTE_LABEL_STYLE}>
        Internal note{" "}
        <span style={{ color: "rgba(0, 0, 0, 0.45)", fontWeight: 400 }}>
          (admin-only, not shown to member)
        </span>
      </div>
      <TextArea
        value={internalNote || ""}
        placeholder="Optional context for other reviewers..."
        onChange={({ target: { value } }): void =>
          setInternalNote(value || null)
        }
        style={TEXT_AREA_STYLE}
        rows={2}
      />
    </div>
  );
};

export default React.forwardRef(ReviewARSubmissionInput);
